// ============================================================================
// client/pet-allure.ts — CE QUI FAIT QU'UN COMPAGNON EST VIVANT
// ----------------------------------------------------------------------------
// Demande de Claude-Alain (11/08) : « crée plus d'animations des pets ».
//
// L'existant tenait en QUATRE poses figées (`ALLURES` dans pet-papier.ts), choisies par un index.
// Deux servaient presque tout le temps, en alternance sèche : le résultat lisait comme une
// marionnette à deux images. Surtout, la pose ne dépendait de rien — ni de la vitesse, ni du saut,
// ni du fait que la bête vole ou marche.
//
// Ici, la pose est CONTINUE et calculée à partir de l'état réel. Trois raisons d'en faire un
// module à part, pur, sans canvas ni horloge :
//   1. il se teste ligne à ligne (tests/pet-allure) — une animation ratée est sinon invisible ;
//   2. il reste UN seul modèle d'animation pour les treize. Pas de branche par bête : c'est
//      exactement ce qui a laissé trois familiers sans taille et sans vol pendant douze jours ;
//   3. il s'applique au TIRAGE, pas au cache. Une pose ne coûte donc pas un dessin de plus.
//
// Le vocabulaire est celui de l'animation traditionnelle : « squash & stretch » (on s'étire en
// s'élançant, on s'écrase en retombant) et « follow through » (le corps continue sur sa lancée
// quand les pattes se posent — c'est le cisaillement).
// ============================================================================

/** Ce que le rendu sait de la bête à cet instant. Tout est déjà calculé par pet.ts. */
export interface EtatAllure {
  /** temps en ticks, continu (tick + alpha) : anime ce qui ne dépend pas des pas */
  t: number
  /** avancement du cycle de pas, 0 à 1 — lié à la DISTANCE parcourue, pas au temps :
   *  une bête qui avance lentement pose ses pattes lentement */
  phase: number
  volant: boolean
  idle: boolean
  special: boolean
  react: 'joy' | 'dig' | 'cuddle' | null
  mourning: boolean
  /** progression du saut de 0 (décollage) à 1 (réception) ; -1 = pas de saut */
  hop: number
}

/** Une pose : la pièce de papier entière basculée, étirée et cisaillée autour de ses pieds. */
export interface PosePet {
  angle: number  // inclinaison, radians
  sx: number     // échelle horizontale
  sy: number     // échelle verticale
  dy: number     // décalage vertical, pixels (négatif = en l'air)
  skew: number   // cisaillement horizontal : le haut du corps traîne derrière les pattes
}

// ⚠️ LA CHAUVE-SOURIS EN FAIT PARTIE (11/08). Elle en était absente depuis son arrivée le 30/07 :
// elle trottait donc au sol en soulevant de la poussière, sans jamais battre des ailes (la frame
// d'aile ne se calcule que pour les volants). Un contresens qui se voyait, une fois qu'elle a
// enfin été peinte.
export const FLYING_PETS = new Set(['pet-canari', 'pet-perroquet', 'pet-chouette', 'pet-dragonnet',
  'pet-follet', 'pet-chauve-souris'])
// Taille de chaque compagnon, en fraction de case (48 px). La bande utile est 22-35 px : en
// dessous la silhouette ne se lit plus, au-dessus elle vole la vedette au héros.
// ⚠️ Une bête ABSENTE d'ici tombait sur 0,6 sans que personne ne l'ait décidé — c'était le cas des
// trois du 30/07. Un test exige maintenant une entrée par familier du catalogue.
export const PET_SCALE: Record<string, number> = {
  'pet-chat': 0.62, 'pet-canari': 0.46, 'pet-loup': 0.72, 'pet-perroquet': 0.54,
  'pet-araignee': 0.54, 'pet-serpent': 0.66, 'pet-chouette': 0.58,
  // DEUX FOIS PLUS GRAND (31/07, demande Claude-Alain). Un dragon a besoin de place : un long
  // cou, des ailes déployées et une queue enroulée ne tiennent pas dans 28 px. Il dépasse
  // maintenant le héros — c'est voulu, c'est le seul compagnon « méga » qui en impose.
  'pet-dragonnet': 1.16,
  'pet-renard': 0.62, 'pet-follet': 0.46,
  // LES TROIS DU 30/07, enfin réglés (11/08). Chacun pour une raison de silhouette :
  // LES TROIS DU 13/08, réglés à la silhouette comme les autres :
  'pet-herisson': 0.54,       // une boule de piquants : compacte, elle n'a pas besoin de place
  'pet-axolotl': 0.60,        // ses branchies débordent de chaque côté de la tête
  'pet-cameleon': 0.62,       // la queue enroulée compte dans la silhouette, et elle est large
  'pet-taupe': 0.56,          // basse et ramassée, elle rase le sol : la faire grande la rendrait bovine
  'pet-chauve-souris': 0.62,  // c'est l'ENVERGURE qui la fait reconnaître, il lui faut de la largeur
  'pet-crapaud': 0.60,        // large mais très bas : à surface égale il paraît plus petit qu'un chat
}

const TAU = Math.PI * 2
/** Garde-fou : aucune pose ne doit pouvoir faire disparaître ni retourner une bête. */
const borne = (v: number, min: number, max: number): number => v < min ? min : v > max ? max : v

export function posePet(e: EtatAllure): PosePet {
  let angle = 0, sx = 1, sy = 1, dy = 0, skew = 0

  if (e.mourning) {
    // DEUIL : elle s'assoit près du corps, tassée, la tête basse. Une respiration très lente
    // suffit à dire qu'elle est vivante, elle.
    const souffle = Math.sin(e.t * 0.045)
    return { angle: -0.05, sx: 1.05, sy: 0.9 + souffle * 0.012, dy: 0, skew: 0.04 }
  }

  if (e.idle) {
    // REPOS. C'est l'état où on regarde le plus son compagnon — il attend à côté de vous — et
    // c'était le plus figé de tous : une image immobile. Mesuré au harnais, un terrestre à l'arrêt
    // ne faisait bouger que trois pixels d'une image à l'autre.
    // Deux mouvements de PÉRIODES DIFFÉRENTES, pour que l'ensemble ne batte jamais la mesure :
    // la respiration (ventre qui se gonfle, épaules d'un souffle en retard) et un dandinement
    // latéral plus lent. Le second passe par le cisaillement plutôt que par l'inclinaison : une
    // bête au repos ne penche pas, elle se balance.
    const souffle = Math.sin(e.t * 0.075)
    sy = 1 + souffle * 0.045
    sx = 1 - souffle * 0.032
    dy = -souffle * 1.5
    skew = 0.035 * Math.sin(e.t * 0.055)
    if (e.special) {
      // ACTION SPÉCIALE : elle se dresse, s'étire vers le haut et se penche dans son geste.
      sy += 0.07; sx -= 0.04; dy -= 1.6; angle = -0.035
    }
  } else {
    // TROT : un vrai cycle de pas. Deux appuis par foulée, donc tout ce qui touche le sol bat à
    // la double fréquence de ce qui bascule.
    const a = e.phase * TAU
    angle = -0.085 * Math.cos(a)              // plonge en avant, puis se redresse
    dy = -2.6 * Math.abs(Math.sin(a))         // rebond : elle décolle entre deux appuis
    sy = 1 - 0.055 * Math.cos(2 * a)          // écrasée à l'appui, étirée en suspension
    sx = 1 + 0.045 * Math.cos(2 * a)
    skew = 0.055 * Math.sin(a)                // le corps traîne derrière les pattes
  }

  if (e.volant) {
    // VOL : le battement d'ailes prime sur tout le reste. Un cycle rapide (≈ 6 ticks), une montée
    // franche et un tangage léger — c'est le déplacement vertical qui fait lire l'aile, pas le
    // détail du dessin à cette taille.
    const b = e.t * (TAU / 6)
    dy += -2.4 * Math.sin(b)
    sy = 1 + 0.05 * Math.sin(b)
    sx = 1 - 0.04 * Math.sin(b)
    angle = (e.idle ? 0 : angle * 0.35) + 0.045 * Math.sin(b + Math.PI / 2)
    skew *= 0.4
  }

  if (e.react === 'joy') {
    // JOIE : elle sautille sur place, plus haut et plus vite que son trot, en se tortillant.
    const j = e.t * 0.42
    dy += -3.4 * Math.abs(Math.sin(j))
    angle += 0.06 * Math.sin(j * 2)
    sy += 0.05 * Math.sin(j * 2)
  } else if (e.react === 'dig') {
    // GRATTAGE : le train avant pioche. Secousses courtes, penchée vers l'avant.
    angle += -0.09 + 0.05 * Math.sin(e.t * 0.9)
    dy += 0.6 * Math.abs(Math.sin(e.t * 0.9))
    skew += 0.05
  } else if (e.react === 'cuddle') {
    // CÂLIN : elle se frotte contre son maître, appuyée sur le côté, sans presque bouger les pieds.
    angle += 0.1 * Math.sin(e.t * 0.12)
    skew += 0.09 * Math.sin(e.t * 0.12)
    sy = Math.min(sy, 0.99)
  }

  if (e.hop >= 0) {
    // SAUT : squash & stretch. On s'étire en s'élançant, on est neutre au sommet, on s'écrase
    // en retombant. C'est la partie de l'animation qui se remarque le plus, pour le moins d'effort.
    const p = borne(e.hop, 0, 1)
    // +1 au départ, 0 au sommet, -1 à la réception
    const etirement = Math.cos(p * Math.PI)
    sy += etirement * 0.11
    sx -= etirement * 0.09
  }

  return {
    angle: borne(angle, -0.4, 0.4),
    sx: borne(sx, 0.55, 1.55),
    sy: borne(sy, 0.55, 1.55),
    dy: borne(dy, -13, 13),
    skew: borne(skew, -0.28, 0.28),
  }
}
